import { useState, useEffect } from 'react';
import { Save, AlertCircle, CheckCircle, Clock, Send } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { ChecklistSection } from './ChecklistSection';
import { ItemStatus } from './ChecklistItem';
import { INSPECTION_CHECKLIST } from '../../utils/inspectionChecklistData';

interface ItemState {
  status: ItemStatus;
  notes?: string;
}

interface InspectionChecklistProps {
  inspectionId: string;
  propertyName: string;
  onComplete: () => void;
}

export function InspectionChecklist({
  inspectionId,
  propertyName,
  onComplete,
}: InspectionChecklistProps) {
  const [itemStates, setItemStates] = useState<Record<string, ItemState>>({});
  const [maintenanceIssues, setMaintenanceIssues] = useState('');
  const [generalNotes, setGeneralNotes] = useState('');
  const [inspectorSignature, setInspectorSignature] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [timeStarted, setTimeStarted] = useState('');
  const [sending, setSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    loadInspectionData();
    const now = new Date();
    setTimeStarted(now.toTimeString().slice(0, 5));
  }, [inspectionId]);

  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      saveProgress();
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [itemStates, maintenanceIssues, generalNotes, inspectorSignature]);

  const loadInspectionData = async () => {
    try {
      const { data: inspection } = await supabase
        .from('inspections')
        .select('*, inspection_items(*)')
        .eq('id', inspectionId)
        .single();

      if (inspection) {
        setMaintenanceIssues(inspection.maintenance_issues || '');
        setGeneralNotes(inspection.general_notes || '');
        setInspectorSignature(inspection.inspector_signature || '');
        setTimeStarted(inspection.time_started || '');

        const states: Record<string, ItemState> = {};
        inspection.inspection_items?.forEach((item: any) => {
          states[item.id] = {
            status: item.status,
            notes: item.notes,
          };
        });
        setItemStates(states);
      }
    } catch (error) {
      console.error('Error loading inspection:', error);
    }
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      const totalItems = INSPECTION_CHECKLIST.reduce(
        (sum, section) =>
          sum + section.subsections.reduce((subSum, subsection) => subSum + subsection.items.length, 0),
        0
      );

      const checkedItems = Object.values(itemStates).filter(
        (state) => state.status !== 'unchecked'
      ).length;

      const completionPercentage = Math.round((checkedItems / totalItems) * 100);

      await supabase
        .from('inspections')
        .update({
          completion_percentage: completionPercentage,
          maintenance_issues: maintenanceIssues,
          general_notes: generalNotes,
          inspector_signature: inspectorSignature,
          time_started: timeStarted,
          status: completionPercentage === 100 ? 'completed' : 'in_progress',
        })
        .eq('id', inspectionId);

      for (const [itemId, state] of Object.entries(itemStates)) {
        const item = INSPECTION_CHECKLIST.flatMap((section) =>
          section.subsections.flatMap((subsection) => subsection.items)
        ).find((i) => i.id === itemId);

        if (item) {
          const subsection = INSPECTION_CHECKLIST.flatMap((section) =>
            section.subsections.map((s) => ({ section: section.category, subsection: s.title, items: s.items }))
          ).find((s) => s.items.some((i) => i.id === itemId));

          await supabase.from('inspection_items').upsert({
            id: itemId,
            inspection_id: inspectionId,
            category: subsection?.section || '',
            subcategory: subsection?.subsection || '',
            item_text: item.text,
            item_order: item.order,
            status: state.status,
            notes: state.notes,
          });
        }
      }

      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving inspection:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleItemChange = (itemId: string, status: ItemStatus, notes?: string) => {
    setItemStates((prev) => ({
      ...prev,
      [itemId]: { status, notes },
    }));
  };

  const handleComplete = async () => {
    await saveProgress();

    const now = new Date();
    const timeCompleted = now.toTimeString().slice(0, 5);

    await supabase
      .from('inspections')
      .update({
        status: 'completed',
        time_completed: timeCompleted,
        completion_percentage: 100,
      })
      .eq('id', inspectionId);

    onComplete();
  };

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const { data: inspection } = await supabase
        .from('inspections')
        .select('*, properties(name, address)')
        .eq('id', inspectionId)
        .single();

      if (!inspection) throw new Error('Inspection not found');

      const checklistData: Record<string, Record<string, { status: string; notes?: string }>> = {};

      INSPECTION_CHECKLIST.forEach((section) => {
        checklistData[section.category] = {};
        section.subsections.forEach((subsection) => {
          subsection.items.forEach((item) => {
            const state = itemStates[item.id];
            if (state && state.status !== 'unchecked') {
              checklistData[section.category][item.text] = {
                status: state.status,
                notes: state.notes,
              };
            }
          });
        });
      });

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-inspection-email`;
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inspectionId,
          propertyName: inspection.properties?.name || 'Unknown Property',
          address: inspection.properties?.address || 'N/A',
          inspectionDate: inspection.inspection_date,
          checklistData,
          photos: [],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const getTotalStats = () => {
    const total = INSPECTION_CHECKLIST.reduce(
      (sum, section) =>
        sum + section.subsections.reduce((subSum, subsection) => subSum + subsection.items.length, 0),
      0
    );

    const checked = Object.values(itemStates).filter((state) => state.status !== 'unchecked').length;
    const passed = Object.values(itemStates).filter((state) => state.status === 'pass').length;
    const failed = Object.values(itemStates).filter((state) => state.status === 'fail').length;
    const attention = Object.values(itemStates).filter(
      (state) => state.status === 'needs_attention'
    ).length;

    return { total, checked, passed, failed, attention };
  };

  const stats = getTotalStats();
  const percentage = stats.total > 0 ? Math.round((stats.checked / stats.total) * 100) : 0;

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Property Inspection Checklist</h1>
            <p className="text-sm text-gray-600 mt-1">{propertyName}</p>
          </div>
          <div className="flex items-center gap-3">
            {lastSaved && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <Clock size={14} />
                <span>Saved {lastSaved.toLocaleTimeString()}</span>
              </div>
            )}
            <button
              onClick={saveProgress}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Progress'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-800">{stats.checked}/{stats.total}</div>
            <div className="text-xs text-gray-600">Items Checked</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-700">{stats.passed}</div>
            <div className="text-xs text-green-600">Passed</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-700">{stats.failed}</div>
            <div className="text-xs text-red-600">Failed</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-yellow-700">{stats.attention}</div>
            <div className="text-xs text-yellow-600">Needs Attention</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{percentage}%</div>
            <div className="text-xs text-blue-600">Complete</div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {INSPECTION_CHECKLIST.map((section, index) => (
          <ChecklistSection
            key={index}
            section={section}
            itemStates={itemStates}
            onItemChange={handleItemChange}
          />
        ))}

        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-6">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle className="text-yellow-600 flex-shrink-0 mt-1" size={20} />
            <h2 className="text-xl font-bold text-gray-800">Maintenance Issues Identified</h2>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Note any minor repairs needed: walls, cabinets, appliances, furniture, etc.
          </p>
          <textarea
            value={maintenanceIssues}
            onChange={(e) => setMaintenanceIssues(e.target.value)}
            placeholder="Priority action needed..."
            className="w-full px-4 py-3 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
            rows={5}
          />
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-3">General Notes</h2>
          <textarea
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
            placeholder="Enter any additional notes or observations here..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={6}
          />
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Complete Inspection</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Inspector Signature
              </label>
              <input
                type="text"
                value={inspectorSignature}
                onChange={(e) => setInspectorSignature(e.target.value)}
                placeholder="Type your name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Started</label>
              <input
                type="time"
                value={timeStarted}
                onChange={(e) => setTimeStarted(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <button
              onClick={handleSendEmail}
              disabled={sending || emailSent}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={20} />
              {sending ? 'Sending...' : emailSent ? 'Email Sent!' : 'Send Report to jerome@colorado-hospitality.com'}
            </button>
            <button
              onClick={handleComplete}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              <CheckCircle size={20} />
              Mark Inspection as Complete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
